// Capture-phase framing thresholds. Owned by docs/gameplay-config.md#capture.

export const CAPTURE_RESOLUTION_MAX = 2048;
export const UPLOAD_RESOLUTION = 512;

// All framing fractions below are now measured in AREA-2 viewport space (the
// 9fr viewfinder the user actually looks at), NOT the full video frame. The face
// bbox is projected into area-2-relative fractions via capture/cover-map.ts.
// See framing.ts#checkFramingArea2 and docs/gameplay-config.md#capture.

// Max distance of the face-bbox center from area-2 center, as a fraction of the
// area-2 edge (applied on x and y independently).
export const FRAMING_FACE_CENTER_TOLERANCE = 0.2;

// Acceptable face-bbox WIDTH as a fraction of area-2 width. Width (not max edge)
// is the basis because it is stable under the cover mapping and mirror-invariant.
// 2026-06 new-flow viewfinder is WIDER than a face (a portrait face fills the
// box's height before its width), so the old 0.50 width-min made any face that
// passed it too tall → it always tripped the crown check (empty pass band).
// Lowered the min so a comfortably-sized face leaves vertical room for the crown.
export const FRAMING_FACE_MIN_FRACTION = 0.3;
export const FRAMING_FACE_MAX_FRACTION = 0.92;

// Alignment-guide oval geometry (capture/guide-overlay.ts). The oval is the
// visible target inside the pass band; width as a fraction of area-2 width, with
// height = width × aspect (faces are taller than wide).
export const GUIDE_OVAL_WIDTH_FRACTION = 0.7;
export const GUIDE_OVAL_ASPECT = 1.3;

export const FRAMING_REQUIRE_HEAD_TOP = true;
// Vertical fit: the WHOLE head must sit inside area-2 or the capture clips the
// crown or chin. MediaPipe's face bbox stops at the hairline (top) and chin
// (bottom) — it does not include the hair/crown — so the bbox top underestimates
// the real top of the head. We pad the bbox upward by FRAMING_CROWN_PAD × the
// bbox height to estimate the crown, then require that estimated head-top and the
// chin to stay inside area-2 with a small margin. This is the binding "too close"
// constraint (a face bbox is ~1.4× taller than wide, so vertical extent caps size
// well before the width-based FRAMING_FACE_MAX_FRACTION does).
// Crown padding relaxed for the wider new-flow viewfinder so the pass band
// (between too_small and head_clipped) is comfortably non-empty.
export const FRAMING_CROWN_PAD = 0.12;    // crown height as a fraction of bbox height
export const FRAMING_TOP_MARGIN = 0.0;    // min gap above the estimated crown (area-2 fraction)
export const FRAMING_BOTTOM_MARGIN = 0.0; // min gap below the chin (area-2 fraction)
// Max head tilt before the capture is rejected. Measured as the angle of
// the line between the eye outer corners (MediaPipe landmarks 33 ↔ 263).
// 8° is loose enough to feel natural, strict enough that the BE pipeline
// produces level head sprites without needing a server-side rotation step.
export const FRAMING_MAX_TILT_DEGREES = 8;

// "Look at the camera": max head yaw (turning left/right) and pitch (looking
// up/down) before the capture is rejected. Distinct from tilt (head roll). The
// BE touch-up/compositing assume a roughly front-facing head, so off-axis poses
// must be gated out. Derived from MediaPipe's facial transformation matrix
// (capture/head-pose.ts). Conservative starting values — tune on-device against
// the live yaw/pitch readout in the ?cam-debug overlay.
export const FRAMING_MAX_YAW_DEGREES = 18;
export const FRAMING_MAX_PITCH_DEGREES = 15;
